/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { FunctionComponent } from "react";
import { styled } from "@iventis/styles";
import { Formik } from "formik";
import * as Yup from "yup";
import { Content } from "@iventis/translations/content/typed-content";
import { TextField } from "@mui/material";
import { Theme, css } from "@emotion/react";
import { SolidButton } from "@iventis/styles/src/components/button";
import { StyledFieldLabel } from "@iventis/styles/src/components/forms";
import { Body2, FormLabel } from "@iventis/styles/src/components/texts";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { passwordHeaderMargin } from "@iventis/styles/src/atomic-rules";
import { dataTestIds } from "@iventis/testing";
import { StyledLoginForm } from "./login-form";

interface LoginEmailFormProps {
    emailInput: string;
    setEmailInput: (emailInput: string) => void;
    onConfirmEmailClicked: (email: string) => void;
}

const LoginEmailForm: FunctionComponent<LoginEmailFormProps> = ({ emailInput, setEmailInput, onConfirmEmailClicked }) => {
    // "Email not recognised" logic doesn't exist yet
    const enteredWrong = false;
    const translate = useIventisTranslate();
    interface Values {
        email: string;
    }
    return (
        <>
            <Formik
                initialValues={{
                    email: emailInput,
                }}
                validationSchema={Yup.object({
                    email: Yup.string().email(translate(Content.login.error.invalid)).required(translate(Content.login.error.required)),
                })}
                onSubmit={async (values: Values) => {
                    setEmailInput(values.email);
                    onConfirmEmailClicked(values.email);
                }}
            >
                {(props) => {
                    const { errors, values, touched, handleChange, handleBlur, handleSubmit, isSubmitting } = props;
                    return (
                        <StyledEmailForm data-testid="login-email-form" onSubmit={handleSubmit}>
                            <div style={{ height: "80px" }}>
                                <StyledFieldLabel id="email-label" data-testid="email-label" htmlFor="email-input">
                                    <FormLabel>{translate(Content.login.labels.email)}</FormLabel>
                                </StyledFieldLabel>
                                <TextField
                                    id="email-input"
                                    variant="outlined"
                                    aria-labelledby="email-label"
                                    name="email"
                                    data-cy="email"
                                    type="email"
                                    style={{ minWidth: "100%" }}
                                    error={errors.email && touched.email}
                                    value={values.email}
                                    placeholder={translate(Content.login.placeholders.email)}
                                    inputProps={{
                                        "data-testid": dataTestIds.login.emailInput,
                                    }}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    helperText={errors.email && touched.email && errors.email}
                                    autoComplete="username"
                                />
                                {enteredWrong && <EmailError />}
                            </div>
                            <ButtonContainer>
                                <StyledResetButton
                                    disabled={isSubmitting}
                                    id="continue-button"
                                    data-cy="continue-button"
                                    data-testid={dataTestIds.login.continueButton}
                                    type="submit"
                                >
                                    {translate(Content.login.buttons.continue)}
                                </StyledResetButton>
                            </ButtonContainer>
                        </StyledEmailForm>
                    );
                }}
            </Formik>
        </>
    );
};

const StyledEmailForm = styled(StyledLoginForm)`
    .email-error {
        margin: 20px 0 10px 0;
    }
`;

const ButtonContainer = styled.span`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    padding: ${passwordHeaderMargin} 0 0 0;
`;

const EmailError = () => {
    const translate = useIventisTranslate();
    return (
        <StyledEmailErrorRect className="email-error">
            <Body2>{translate(Content.login.error.cantFindEmail)}</Body2>
        </StyledEmailErrorRect>
    );
};

const StyledEmailErrorRect = styled.div`
    ${({ theme }: { theme: Theme }) => css`
        background-color: ${theme.secondaryColors.lightError};
    `};
    padding: 11px 11px 11px 18px;
    border-radius: 4px;
`;

export const StyledResetButton = styled(SolidButton)`
    display: flex;
    width: 158px;
    align-self: flex-end;
`;

export default LoginEmailForm;
